import React, { useState } from 'react';
import {
  Modal,
  Paper,
  Typography,
  Button,
  Box,
  Divider,
  Stack,
  IconButton,
  Chip,
  Grid,
  Tab,
  Tabs,
  Collapse
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import PaymentIcon from '@mui/icons-material/Payment';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsIcon from '@mui/icons-material/Directions';
import CategoryIcon from '@mui/icons-material/Category';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PrizesTable from './PrizesTable';
import TournamentResultsTable from './TournamentResultsTable';
import { styles } from './styles';

// Funkcja pomocnicza do obliczania odległości
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Promień Ziemi w kilometrach
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Odległość w km
};

// Custom TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Komponent do wyświetlania struktury turnieju
const TournamentStructure = ({ structure }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!structure) return null;
  
  // Sprawdź czy opis jest długi (więcej niż 100 znaków)
  const isLongDescription = structure.length > 100;
  
  // Funkcja do formatowania tekstu struktury
  const formatStructureText = (text) => {
    // Podziel tekst na linie na podstawie znaków nowej linii
    const lines = text.split('\n').filter(line => line.trim());
    
    return lines.map((line, index) => {
      // Sprawdź czy linia zaczyna się od dnia tygodnia
      const dayMatch = line.match(/^(Saturday|Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Sobota|Niedziela|Poniedziałek|Wtorek|Środa|Czwartek|Piątek)/i);
      
      if (dayMatch) {
        return (
          <Box key={index} sx={{ mb: 1.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'rgb(196, 181, 253)',
                fontWeight: '600',
                fontSize: '0.9rem',
                mb: 0.5
              }}
            >
              {dayMatch[0]}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'white',
                ml: 1.5,
                lineHeight: 1.5,
                fontSize: '0.875rem'
              }}
            >
              {line.substring(dayMatch[0].length).trim()}
            </Typography>
          </Box>
        );
      }
      
      // Zwykła linia tekstu
      return (
        <Typography
          key={index}
          variant="body2"
          sx={{
            color: 'white',
            lineHeight: 1.5,
            mb: 0.5,
            fontSize: '0.875rem'
          }}
        >
          {line}
        </Typography>
      );
    });
  };

  return (
    <Stack direction="row" alignItems="flex-start" gap={1} sx={{ mb: 2 }}>
      <FormatListNumberedIcon 
        fontSize='small' 
        sx={{ 
          color: 'rgb(196, 181, 253)', 
          mt: '3px',
          flexShrink: 0
        }} 
      />
      <Box sx={{ flex: 1 }}>
        {isLongDescription ? (
          <>
            <Collapse in={expanded} timeout="auto">
              <Box>
                {formatStructureText(structure)}
              </Box>
            </Collapse>
            
            {!expanded && (
              <Typography
                component="span"
                sx={{
                  color: 'white',
                  lineHeight: 1.5,
                  fontSize: '0.875rem'
                }}
              >
                {structure.substring(0, 100)}...
              </Typography>
            )}
            
            <Button
              onClick={() => setExpanded(!expanded)}
              size="small"
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                color: 'rgb(196, 181, 253)',
                textTransform: 'none',
                mt: 0.5,
                p: 0,
                minWidth: 'unset',
                fontSize: '0.75rem',
                display: 'block',
                justifyContent: 'flex-start',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'white'
                }
              }}
            >
              {expanded ? 'Pokaż mniej' : 'Pokaż więcej'}
            </Button>
          </>
        ) : (
          <Typography
            component="span"
            sx={{
              color: 'white',
              lineHeight: 1.5,
              fontSize: '0.875rem'
            }}
          >
            {structure}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

const EventDetailsModal = ({ event, open, onClose, userLocation }) => {
  const [tabValue, setTabValue] = useState(0);

  if (!event) return null;

  // Oblicz odległość, jeśli są dostępne dane lokalizacyjne
  let distance = null;
  if (userLocation) {
    if (event.customLocation?.coordinates) {
      distance = calculateDistance(
        userLocation.lat,
        userLocation.lng,
        event.customLocation.coordinates.lat,
        event.customLocation.coordinates.lng
      );
    } else if (event.organizer && event.organizer.coordinates) {
      distance = calculateDistance(
        userLocation.lat,
        userLocation.lng,
        event.organizer.coordinates.lat,
        event.organizer.coordinates.lng
      );
    }
  }

  // Funkcja do otwierania Google Maps z trasą
  const openDirections = () => {
    if (event.customLocation?.coordinates) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${event.customLocation.coordinates.lat},${event.customLocation.coordinates.lng}`, '_blank');
    } else if (event.organizer && event.organizer.coordinates) {
      const { lat, lng } = event.organizer.coordinates;
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
    }
  };

  // Check if tournament results are available
  const hasResults = event.results && Object.keys(event.results).length > 0;

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Funkcja do otwierania profilu melee.gg
  const openMeleeProfile = () => {
    if (event.meleeLink) {
      window.open(event.meleeLink, '_blank', 'noopener,noreferrer');
    }
  };

  // Przykładowe dane do celów demonstracyjnych
  const tournamentInfo = {
    gameMode: event.format || "",
    structure: event.structure || "",
    prizes: event.prizes || ""
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          ...styles.modalContainer,
          width: ((!event.prizes || Object.keys(event.prizes).length === 0) && !hasResults) ? { xs: '95%', sm: '420px' } : { xs: '90%', sm: '70%' },
          maxWidth: ((!event.prizes || Object.keys(event.prizes).length === 0) && !hasResults) ? 480 : 'none',
          margin: '0 auto',
          maxHeight: '85vh'
        }}
      >
        {/* Nagłówek z przyciskiem zamknięcia */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', fontWeight: '600' }}>
            {event.isMultiDayCopy ? `${event.name} (Dzień ${event.dayNumber})` : event.name}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Układ dwukolumnowy dla desktopa */}
        <Grid container spacing={3}>
          {/* Lewa kolumna - informacje o turnieju */}
          <Grid
            item
            xs={12}
            sm={(event.prizes && Object.keys(event.prizes).length > 0) || hasResults ? 4 : 12}
            sx={((!event.prizes || Object.keys(event.prizes).length === 0) && !hasResults) ? {
              maxWidth: 420,
              margin: '0 auto',
              float: 'none'
            } : {}}
          >
            <Typography variant="subtitle1" sx={{
              fontWeight: 'bold',
              color: 'white',
              mb: 2
            }}>
              Informacje o turnieju
            </Typography>

            {/* Data i godzina */}
            <Stack direction="row" alignItems="flex-start" gap={1} sx={{ mb: 2 }}>
              <EventIcon fontSize='small' sx={{ color: 'rgb(196, 181, 253)', mt: '3px' }} />
              <Typography component="span" sx={{ color: 'white' }}>
                {new Date(event.date).toLocaleDateString('pl-PL')} {event.time}
              </Typography>
            </Stack>

            {/* Informacja o turnieju regularnym */}
            {event.isRegular && (
              <Stack direction="row" alignItems="flex-start" gap={1} sx={{ mb: 2 }}>
                <EventIcon fontSize='small' sx={{ color: 'rgb(34, 197, 94)', mt: '3px' }} />
                <Box>
                  <Typography component="span" sx={{ 
                    color: 'rgb(34, 197, 94)', 
                    fontWeight: '600',
                    fontSize: '0.875rem'
                  }}>
                    Wydarzenie cykliczne - {event.dayOfWeek}
                  </Typography>
                  <Typography component="div" sx={{ 
                    color: 'rgb(156, 163, 175)', 
                    fontSize: '0.75rem',
                    mt: 0.5,
                    fontStyle: 'italic'
                  }}>
                    Wydarzenia cykliczne dodawane są do kalendarza automatycznie. Mogą nie odbywać się w dni wolne od pracy albo zostać zastąpione przez turnieje wyższej rangi. W razie wątpliwości sprawdź stronę lub media społecznościowa organizatora przy pomocy przycisku "Więcej informacji" poniżej.
                  </Typography>
                </Box>
              </Stack>
            )}

            {/* Cena za udział */}
            {event.price && (
              <Stack direction="row" alignItems="flex-start" gap={1} sx={{ mb: 2 }}>
                <PaymentIcon fontSize='small' sx={{ color: 'rgb(196, 181, 253)', mt: '3px' }} />
                <Typography component="span" sx={{ color: 'white' }}>
                  {typeof event.price === 'number' ? `${event.price} zł` : event.price}
                </Typography>
              </Stack>
            )}

            {/* Tryb gry */}
            <Stack direction="row" alignItems="flex-start" gap={1} sx={{ mb: 2 }}>
              <CategoryIcon fontSize='small' sx={{ color: 'rgb(196, 181, 253)', mt: '3px' }} />
              <Typography component="span" sx={{ color: 'white' }}>
                {tournamentInfo.gameMode}
              </Typography>
            </Stack>

            {/* Struktura turnieju - nowy komponent */}
            <Box sx={{ mb: 2 }}>
              <TournamentStructure structure={tournamentInfo.structure} />
            </Box>

            {/* Liczba dni turnieju */}
            {event.duration && event.duration > 1 && (
              <Stack direction="row" alignItems="flex-start" gap={1} sx={{ mb: 2 }}>
                <EventIcon fontSize='small' sx={{ color: 'rgb(196, 181, 253)', mt: '3px' }} />
                <Typography component="span" sx={{ color: 'white' }}>
                  Turniej {event.duration}-dniowy
                </Typography>
              </Stack>
            )}

            {/* Lokalizacja i adres */}
            <Stack direction="row" alignItems="flex-start" gap={1} sx={{ mb: 2 }}>
              <LocationOnIcon fontSize="small" sx={{ color: 'rgb(196, 181, 253)', mt: '3px' }} />
              <Box>
                <Typography component="span" sx={{ color: 'white', display: 'block' }}>
                  {event.customLocation ? event.customLocation.name : event.organizer?.name}
                </Typography>
                <Typography component="span" sx={{ color: 'rgb(156, 163, 175)', fontSize: '0.875rem', display: 'block' }}>
                  {event.customLocation ? event.customLocation.address : event.organizer?.address}
                </Typography>
                {distance && (
                  <Typography component="span" sx={{ color: 'rgb(196, 181, 253)', fontSize: '0.75rem', display: 'block' }}>
                    {distance.toFixed(1)} km stąd
                  </Typography>
                )}
                <Button
                  variant="contained"
                  startIcon={<DirectionsIcon />}
                  size="small"
                  onClick={openDirections}
                  sx={{
                    backgroundColor: 'rgba(76, 29, 149, 0.7)',
                    color: 'white',
                    fontSize: '0.8rem',
                    textTransform: 'none',
                    padding: '2px 10px',
                    minWidth: 'unset',
                    borderRadius: '6px',
                    mt: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(76, 29, 149, 0.9)'
                    }
                  }}
                >
                  Nawiguj
                </Button>
              </Box>
            </Stack>
          </Grid>

          {/* Prawa kolumna - nagrody i wyniki */}
          {((event.prizes && Object.keys(event.prizes).length > 0) || hasResults) && (
            <Grid item xs={12} sm={8}>
              {/* Tabs for switching between Prizes and Results */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  textColor="inherit"
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: 'rgb(196, 181, 253)'
                    }
                  }}
                  sx={{
                    '& .MuiTab-root': {
                      color: 'rgb(196, 181, 253)',
                      '&.Mui-selected': {
                        color: 'white'
                      }
                    }
                  }}
                >
                  {event.prizes && Object.keys(event.prizes).length > 0 && (
                    <Tab
                      icon={<EmojiEventsIcon />}
                      iconPosition="start"
                      label="Nagrody"
                      sx={{ textTransform: 'none' }}
                    />
                  )}
                  {hasResults && (
                    <Tab
                      icon={<LeaderboardIcon />}
                      iconPosition="start"
                      label="Wyniki"
                      sx={{ textTransform: 'none' }}
                    />
                  )}
                </Tabs>
              </Box>

              {/* Prizes Tab Panel */}
              {event.prizes && Object.keys(event.prizes).length > 0 && (
                <TabPanel value={tabValue} index={0}>
                  <PrizesTable prizes={event.prizes} />
                </TabPanel>
              )}

              {/* Results Tab Panel */}
              {
                hasResults && (
                  <TabPanel value={tabValue} index={1}>
                    <TournamentResultsTable results={event.results} />
                  </TabPanel>
                )
              }
            </Grid >
          )}
        </Grid >

        <Divider sx={{ my: 3 }} />

        {/* Przyciski akcji */}
        <Grid container spacing={2}>
          {/* Przycisk do melee.gg */}
          {event.meleeLink && (
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                fullWidth
                endIcon={
                  <img
                    src="/favicon_melee.ico"
                    alt="melee.gg"
                    style={{
                      width: '20px',
                      height: '20px',
                      filter: 'brightness(0) invert(1)'
                    }}
                  />
                }
                sx={{
                  backgroundColor: 'rgba(76, 29, 149, 0.9)',
                  color: 'white',
                  fontWeight: 'bold',
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'rgba(76, 29, 149, 1)'
                  }
                }}
                onClick={openMeleeProfile}
              >
                Śledź wyniki na melee.gg
              </Button>
            </Grid>
          )}

          {/* Przycisk do dodatkowych informacji */}
          <Grid item xs={12} sm={event.meleeLink ? 6 : 12}>
            <Button
              variant="contained"
              fullWidth
              sx={{
                ...styles.moreInfoButton,
                py: 1.5
              }}
              onClick={() => window.open(event.url, '_blank', 'noopener,noreferrer')}
            >
              Więcej informacji
            </Button>
          </Grid>
        </Grid>
      </Paper >
    </Modal >
  );
};

export default EventDetailsModal;